/**
 * Wattec Custom PC Builder — compatibility & power summary
 *
 * A read-only panel shown alongside Easify's own real option list, not a
 * replacement for it. It never drives a selection itself: browsers only let
 * a genuine customer click register with Easify's own conditional logic and
 * pricing, never a script-driven one — proven by testing a driven three-pane
 * builder against this exact page, where picking a value programmatically
 * left Easify's own conditional groups permanently hidden even though the
 * adapter could see the pick had "happened" in the DOM. So this panel only
 * ever reads what Easify already shows (through the adapter in
 * wattec-pc-builder.js) and redraws itself whenever the customer changes
 * something for real.
 */
(function () {
  'use strict';

  var SOCKET = /\b(AM4|AM5|1851|1700|1200|1151)\b/i;

  // Case, GPU and cooler dimensions/wattage, each read from the specific
  // phrase Wattec's own Easify listings write them in as.
  var CASE_GPU_LENGTH = /Max GPU Length Support:\s*(\d+(?:\.\d+)?)\s*mm/i;
  var CASE_RADIATOR_SUPPORT = /Max AIO Radiator Support:\s*(\d+(?:\.\d+)?)\s*mm/i;
  var CASE_COOLER_HEIGHT = /Max CPU Cooler Height Support:\s*(\d+(?:\.\d+)?)\s*mm/i;
  var GPU_LENGTH = /\bLength:\s*(\d+(?:\.\d+)?)\s*mm/i;
  var COOLER_RADIATOR_LENGTH = /Radiator Length:\s*(\d+(?:\.\d+)?)\s*mm/i;
  var COOLER_HEIGHT = /\bHeight:\s*(\d+(?:\.\d+)?)\s*mm/i;
  var TDP_PATTERN = /TDP\s*[:=]?\s*(\d+(?:\.\d+)?)\s*W\b/i;
  var TDP_SUFFIX_PATTERN = /(\d+(?:\.\d+)?)\s*W\s*TDP\b/i;

  /* A liquid cooler's radiator size, in mm. Sizes are matched as whole
     numbers rather than with a word boundary: in "280mm" there is no boundary
     between the "0" and the "m", so a \b test never matched it — and every
     280mm cooler Wattec lists is named that way, which is why the builder
     behaved as though 280mm coolers stated no size at all. */
  var RADIATOR_SIZE_TOKEN = /(^|[^0-9])(120|140|240|280|360|420)(?![0-9])/g;

  /* Whether a case can mount 140mm fans, which is what a 280mm radiator bolts
     to. Only some listings mention it, so it is used to confirm a fit, never
     to rule one out. */
  var CASE_TAKES_140MM_FANS = /(140\s*mm|2\s*x\s*140|3\s*x\s*140)/i;

  /** The radiator size a cooler states, in mm, or null when it states none.
   *  The largest size mentioned wins, so a cooler that names both its fans and
   *  its radiator ("3 x 120mm fans, 360mm radiator") reports the radiator. The
   *  name is read first and the description only if the name says nothing,
   *  because a description's "Radiator Length" is the length of the whole unit
   *  and reads the same across several different sizes. */
  function radiatorSizeOf(label, spec) {
    var texts = [label, spec];
    for (var i = 0; i < texts.length; i++) {
      var haystack = String(texts[i] || '');
      var best = null;
      var match;
      RADIATOR_SIZE_TOKEN.lastIndex = 0;
      while ((match = RADIATOR_SIZE_TOKEN.exec(haystack)) !== null) {
        var size = parseInt(match[2], 10);
        if (best === null || size > best) best = size;
      }
      if (best !== null) return best;
    }
    return null;
  }

  /**
   * How a cooler's radiator sits in the case that has been chosen.
   *
   * Every radiator size except 280mm is a row of 120mm fans, so comparing it
   * with the case's stated "Max AIO Radiator Support" is exact. A 280mm
   * radiator is the exception: it is a pair of 140mm fans, wider than a 240mm
   * but shorter than a 360mm, so a case's single maximum figure does not
   * settle it on its own.
   *
   *   Case rated 420mm   definitely takes a 280mm.
   *   Case rated 360mm   has the room, and almost all take a 280mm as well.
   *   Case rated 280mm   states it outright.
   *   Case rated 240mm   often takes a 280mm even though its stated maximum is
   *                      smaller, but not always. Confirmed when the listing
   *                      mentions 140mm fans; otherwise flagged so Wattec
   *                      checks it before building.
   *   Case rated 120mm   too small either way.
   */
  function radiatorFitLine(coolerLabel, caseLabel, caseSpec, radiator, caseMax) {
    if (radiator === 280) {
      if (caseMax >= 280) {
        return {
          state: 'ok',
          text: coolerLabel + '\u2019s 280mm radiator fits ' + caseLabel +
            ' (rated for ' + caseMax + 'mm).'
        };
      }
      if (caseMax >= 240) {
        if (CASE_TAKES_140MM_FANS.test(String(caseSpec || ''))) {
          return {
            state: 'ok',
            text: coolerLabel + '\u2019s 280mm radiator fits ' + caseLabel +
              ', which takes the 140mm fans a 280mm radiator needs.'
          };
        }
        return {
          state: 'warn',
          text: coolerLabel + ' has a 280mm radiator and ' + caseLabel + ' is listed for ' +
            caseMax + 'mm. Most cases this size take a 280mm as well, and Wattec will confirm it ' +
            'before building \u2014 but a roomier case is the safe choice if you would rather be sure.'
        };
      }
      return {
        state: 'warn',
        text: coolerLabel + '\u2019s 280mm radiator is bigger than the ' + caseMax + 'mm ' +
          caseLabel + ' supports. Please pick a smaller cooler or a roomier case.'
      };
    }

    var fits = radiator <= caseMax;
    return {
      state: fits ? 'ok' : 'warn',
      text: fits
        ? coolerLabel + '\u2019s radiator fits ' + caseLabel + ' (up to ' + caseMax + 'mm).'
        : coolerLabel + '\u2019s radiator does not fit the space ' + caseLabel +
          ' supports (up to ' + caseMax + 'mm). Please double-check before ordering.'
    };
  }


  /** The first number a pattern finds in a piece of text, or null when the
   *  text doesn't say — a missing figure quietly skips its own check rather
   *  than showing a wrong one. */
  function numberFrom(text, pattern) {
    var match = String(text || '').match(pattern);
    return match ? parseFloat(match[1]) : null;
  }

  /** A component's own stated TDP/wattage rating, tried in either the
   *  "TDP: 145W" order (CPUs, GPUs) or the "145W TDP" order (most coolers
   *  that state one at all). */
  function tdpOf(text) {
    var a = numberFrom(text, TDP_PATTERN);
    return a != null ? a : numberFrom(text, TDP_SUFFIX_PATTERN);
  }

  /**
   * Whether a CPU has integrated graphics, read from its model number rather
   * than assumed from its name — the naming convention flips depending on
   * platform, so the same letter means the opposite thing on AM4 and AM5:
   *
   *   AM4     no integrated graphics by default; a "G" model is the
   *           exception and does have one (e.g. 3200G, 5600G).
   *   AM5     has integrated graphics by default; an "F" model is the
   *           exception and does not (e.g. 7600, 8400F).
   *   Intel   has integrated graphics by default; an "F" model (including
   *           "KF") is the exception and does not.
   *
   * Returns true or false when the platform is recognised, or null when it
   * is not — a CPU from a platform this has never seen is left unjudged
   * rather than guessed at.
   */
  function hasIntegratedGraphics(cpu) {
    var text = String(cpu.label || '');
    var group = String(cpu.optionLabel || '');
    if (/AM4/i.test(group) || /AM4/i.test(text)) return /\d{3,4}G\b/i.test(text);
    if (/AM5/i.test(group) || /AM5/i.test(text)) return !/\d{3,4}F\b/i.test(text);
    if (/Intel|Core|Ultra/i.test(text) || /Gen|Ultra/i.test(group)) return !/\d[K]?F\b/i.test(text);
    return null;
  }

  /** Whether a graphics card needs a 12VHPWR (also called 12V-2x6) power
   *  connector — an explicit mention first, then today's RTX 5070-and-up
   *  reference design as a fallback for cards that don't state it yet. */
  function needsTwelveVHPWR(gpu) {
    var text = String(gpu.label || '') + ' ' + String(gpu.spec || '');
    if (/12\s*V[\s-]?HPWR|12V-?2x6/i.test(text)) return true;
    return /RTX\s*(5070|5080|5090)/i.test(text);
  }

  /** Whether a power supply provides that connector natively — an explicit
   *  mention first, then the ATX 3.0/3.1 or PCIe 5.0/5.1 spec, which the
   *  standard itself requires to include this connector. */
  function hasTwelveVHPWR(psu) {
    var text = String(psu.label || '') + ' ' + String(psu.spec || '');
    if (/12\s*V[\s-]?HPWR|12V-?2x6/i.test(text)) return true;
    if (/ATX\s*3(?:\.\d)?\b/i.test(text)) return true;
    return /PCI-?E?\s*5(?:\.\d)?\b/i.test(text);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function config() {
    var root = document.getElementById('wattec-pc-builder-config');
    if (!root) return {};
    try { return JSON.parse(root.textContent) || {}; } catch (error) { return {}; }
  }

  function money(amount, currency) {
    try {
      return amount.toLocaleString('en-GB', { style: 'currency', currency: currency || 'GBP' });
    } catch (error) {
      return '£' + amount.toFixed(2);
    }
  }

  var POWER_WORDS = {
    low: 'Power headroom looks healthy',
    medium: 'Power supply is working hard',
    high: 'Power supply is too small for this build'
  };

  /** Where the needle points and the bar fills to: the build's own draw as
   *  a share of the supply, capped at the end of the dial. */
  function loadPercent(est) {
    return Math.max(0, Math.min(100, est.percent));
  }

  function svgEl(tag, attrs) {
    var node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    Object.keys(attrs).forEach(function (key) { node.setAttribute(key, attrs[key]); });
    return node;
  }

  /**
   * A half-circle dial: green to 75%, amber to 90%, red beyond, with a
   * needle sitting wherever this build lands. Same reading as the bar
   * below it, just quicker to take in at a glance.
   */
  function buildDial(percent) {
    var svg = svgEl('svg', { viewBox: '0 0 120 68', class: 'wpb-dial__svg' });

    // Each band is an arc of the same circle, drawn thick enough to read.
    function arc(from, to, colour) {
      function point(p) {
        var angle = Math.PI * (1 - p / 100);
        return [60 + 48 * Math.cos(angle), 58 - 48 * Math.sin(angle)];
      }
      var a = point(from);
      var b = point(to);
      // The whole gauge is a half circle: 100 units of scale span 180
      // degrees, so no single band can ever be more than half a circle.
      // The large-arc flag therefore stays 0 — setting it drew each band
      // the long way round the circle instead.
      var large = 0;
      return svgEl('path', {
        d: 'M ' + a[0].toFixed(2) + ' ' + a[1].toFixed(2) +
           ' A 48 48 0 ' + large + ' 1 ' + b[0].toFixed(2) + ' ' + b[1].toFixed(2),
        fill: 'none',
        stroke: colour,
        'stroke-width': '9',
        'stroke-linecap': 'butt'
      });
    }

    svg.appendChild(arc(0, 75, '#4FA86F'));
    svg.appendChild(arc(75, 90, '#D6A93F'));
    svg.appendChild(arc(90, 100, '#D2604C'));

    var angle = Math.PI * (1 - percent / 100);
    svg.appendChild(svgEl('line', {
      x1: '60', y1: '58',
      x2: (60 + 38 * Math.cos(angle)).toFixed(2),
      y2: (58 - 38 * Math.sin(angle)).toFixed(2),
      stroke: '#FCFEFF',
      'stroke-width': '2.5',
      'stroke-linecap': 'round'
    }));
    svg.appendChild(svgEl('circle', { cx: '60', cy: '58', r: '4.5', fill: '#FCFEFF' }));

    var wrap = el('div', 'wpb-dial');
    wrap.appendChild(svg);
    return wrap;
  }

  /**
   * The power reading: what this build draws (processor + graphics card +
   * 100W for the rest of the machine) against the supply chosen for it.
   * Deliberately shows watts rather than a percentage — "420W / 850W" is
   * a figure a customer can act on; "49%" is not.
   */
  function renderPower(target, est) {
    target.innerHTML = '';
    target.appendChild(el('div', 'wpb-power__head', 'SYSTEM POWER'));

    if (!est || !est.ready) {
      target.appendChild(el('div', 'wpb-power__wait',
        'Choose a processor and graphics card to see what this build draws.'));
      return;
    }

    if (!est.psu || !est.psu.watts) {
      target.appendChild(el('div', 'wpb-power__watts', est.systemWatts + 'W'));
      target.appendChild(el('div', 'wpb-power__wait',
        'Choose a power supply to check it has enough headroom.'));
      return;
    }

    var percent = loadPercent(est);

    var reading = el('div', 'wpb-power__reading is-' + est.level);
    reading.appendChild(buildDial(percent));

    var figures = el('div', 'wpb-power__figures');
    figures.appendChild(el('div', 'wpb-power__watts', est.systemWatts + 'W / ' + est.psu.watts + 'W'));
    figures.appendChild(el('div', 'wpb-power__word', POWER_WORDS[est.level]));
    reading.appendChild(figures);
    target.appendChild(reading);

    var track = el('div', 'wpb-power__track');
    var fill = el('div', 'wpb-power__fill is-' + est.level);
    fill.style.width = percent + '%';
    track.appendChild(fill);
    target.appendChild(track);

    if (est.level === 'high') {
      target.appendChild(el('div', 'wpb-power__caution',
        'This build needs more power than the supply chosen can comfortably give. Pick a higher-wattage power supply.'));
    }
  }
  /**
   * A choice that isn't really a choice — Easify pre-fills several optional
   * groups with a placeholder ("Default", "No Thanks - Skip Monitor") so the
   * field is never blank. Those are recorded as chosen, but showing them
   * back to the customer as part of their build is noise, so a placeholder
   * that costs nothing is left out. Anything priced, and any real free
   * choice ("Stock Cooler"), still shows.
   */
  var PLACEHOLDER_STARTS = ['default', 'none', 'no thanks', 'no custom', 'skip'];

  function isPlaceholderPick(item) {
    if (item.price) return false;
    var label = String(item.label || '').trim().toLowerCase();
    for (var i = 0; i < PLACEHOLDER_STARTS.length; i++) {
      if (label.indexOf(PLACEHOLDER_STARTS[i]) === 0) return true;
    }
    return false;
  }

  /**
   * "Your build so far" — every component currently chosen, in the order the
   * builder works through them, with the component's own picture and what it
   * adds to the price. Read straight from the adapter, so it can only ever
   * show what Easify itself has actually recorded.
   */
  function renderPicks(target, adapter, currency) {
    target.innerHTML = '';
    var chosen = adapter.getSelection().filter(function (item) {
      return item.visible && !isPlaceholderPick(item);
    });

    target.appendChild(el('div', 'wpb-picks__head', 'YOUR BUILD SO FAR'));

    if (!chosen.length) {
      target.appendChild(el('div', 'wpb-picks__empty',
        'Nothing chosen yet — your components will appear here as you pick them.'));
      return;
    }

    chosen.forEach(function (item) {
      var row = el('div', 'wpb-picks__row');

      // Category choosers (case size, platform) have no picture of their
      // own — leave the space out rather than show an empty white box.
      if (item.image) {
        var media = el('span', 'wpb-picks__media');
        var img = el('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
        row.appendChild(media);
      }

      var text = el('span', 'wpb-picks__text');
      text.appendChild(el('span', 'wpb-picks__section', item.sectionTitle));
      // Parts chosen by the handful (case fans) say how many.
      text.appendChild(el('span', 'wpb-picks__label',
        (item.quantity > 1 ? item.quantity + ' × ' : '') + item.label));
      row.appendChild(text);

      row.appendChild(el('span', 'wpb-picks__price',
        item.price ? '+' + money(item.price, currency) : 'Included'));

      target.appendChild(row);
    });
  }

  function renderCompat(target, adapter, power) {
    target.innerHTML = '';

    var selection = adapter.getSelection();
    var breakdown = adapter.getPriceBreakdown();
    var bySection = {};
    selection.forEach(function (item) { if (item.visible) bySection[item.section] = item; });

    var cpu = bySection.cpu;
    var board = bySection.motherboard;
    var ram = bySection.memory;
    var caseSize = bySection.case_size;
    var chosenCase = bySection['case'];
    var cooler = bySection.cooling;
    var isLiquidCooler = cooler && /aio|liquid/i.test(cooler.optionLabel);

    var lines = [];

    if (cpu && board) {
      var cpuSocket = (cpu.label + ' ' + cpu.spec).match(SOCKET);
      var boardSocket = (board.label + ' ' + board.spec).match(SOCKET);
      if (cpuSocket && boardSocket) {
        var same = cpuSocket[1].toUpperCase() === boardSocket[1].toUpperCase();
        lines.push({
          state: same ? 'ok' : 'bad',
          text: same
            ? 'Processor and motherboard both use socket ' + cpuSocket[1].toUpperCase() + '.'
            : 'Processor is ' + cpuSocket[1].toUpperCase() + ' but the motherboard is ' +
              boardSocket[1].toUpperCase() + '.'
        });
      }
    }

    var dedicatedGPU = selection.filter(function (item) {
      return item.visible && item.section === 'gpu' && !/graphic.*card.*option/i.test(item.optionLabel);
    })[0];

    if (cpu) {
      var iGPU = hasIntegratedGraphics(cpu);
      if (iGPU === false) {
        var graphicsChoice = selection.filter(function (item) {
          return item.visible && item.section === 'gpu' && /graphic.*card.*option/i.test(item.optionLabel);
        })[0];
        var chosenIntegrated = graphicsChoice && /integrated/i.test(graphicsChoice.label);

        if (chosenIntegrated) {
          lines.push({
            state: 'bad',
            text: cpu.label + ' has no built-in graphics — with "Integrated Graphics" selected, this build ' +
              'would have no picture on screen. Choose a dedicated graphics card instead.'
          });
        } else if (!dedicatedGPU) {
          lines.push({
            state: 'warn',
            text: cpu.label + ' has no built-in graphics, so a dedicated graphics card is needed.'
          });
        }
      }
    }

    if (dedicatedGPU && bySection.psu && needsTwelveVHPWR(dedicatedGPU)) {
      var psuForGpu = bySection.psu;
      lines.push(hasTwelveVHPWR(psuForGpu)
        ? {
            state: 'ok',
            text: dedicatedGPU.label + ' needs a 12VHPWR power connector, and ' + psuForGpu.label + ' has one built in.'
          }
        : {
            state: 'warn',
            text: dedicatedGPU.label + ' needs a 12VHPWR power connector. ' + psuForGpu.label +
              ' does not have one built in, so an adapter will be used.'
          });
    }

    if (ram && board) {
      var ramGen = (ram.label + ' ' + ram.spec).match(/DDR([45])/i);
      var boardGen = (board.label + ' ' + board.spec).match(/DDR([45])/i);
      if (ramGen && boardGen) {
        lines.push({
          state: ramGen[1] === boardGen[1] ? 'ok' : 'bad',
          text: ramGen[1] === boardGen[1]
            ? 'Memory is DDR' + ramGen[1] + ', which this motherboard takes.'
            : 'Memory is DDR' + ramGen[1] + ' but the motherboard takes DDR' + boardGen[1] + '.'
        });
      }
    }

    if (caseSize) {
      lines.push({
        state: 'ok',
        text: 'Only parts that fit a ' + caseSize.label + ' build are being offered.'
      });
    }

    // Physical fit against the case actually chosen: graphics card length,
    // radiator size (liquid cooling) or cooler height (air cooling), and
    // whether the cooler's own wattage rating covers the CPU's. Each only
    // shows once both sides state a figure.
    if (chosenCase && dedicatedGPU) {
      var gpuLength = numberFrom(dedicatedGPU.spec, GPU_LENGTH);
      var caseMaxGpu = numberFrom(chosenCase.spec, CASE_GPU_LENGTH);
      if (gpuLength != null && caseMaxGpu != null) {
        var gpuFits = gpuLength <= caseMaxGpu;
        lines.push({
          state: gpuFits ? 'ok' : 'warn',
          text: gpuFits
            ? dedicatedGPU.label + ' (' + gpuLength + 'mm) fits ' + chosenCase.label + ' (up to ' + caseMaxGpu + 'mm).'
            : dedicatedGPU.label + ' is ' + gpuLength + 'mm long, longer than ' + chosenCase.label +
              ' supports (up to ' + caseMaxGpu + 'mm). Please double-check it will fit before ordering.'
        });
      }
    }

    if (chosenCase && cooler && isLiquidCooler) {
      // A cooler's name is what actually states its radiator: "...360mm
      // Liquid CPU Cooler" is a 360mm radiator and fits any case rated
      // for 360mm. The description's "Radiator Length" is the physical
      // length of the whole unit and reads the same on many different
      // sizes, so it is only used when the name says nothing.
      var radiatorLength = radiatorSizeOf(cooler.label, null);
      if (radiatorLength == null) radiatorLength = numberFrom(cooler.spec, COOLER_RADIATOR_LENGTH);
      var caseMaxRadiator = numberFrom(chosenCase.spec, CASE_RADIATOR_SUPPORT);
      if (radiatorLength != null && caseMaxRadiator != null) {
        lines.push(radiatorFitLine(
          cooler.label, chosenCase.label, chosenCase.spec, radiatorLength, caseMaxRadiator));
      }
    }

    if (chosenCase && cooler && !isLiquidCooler) {
      var coolerHeight = numberFrom(cooler.spec, COOLER_HEIGHT);
      var caseMaxHeight = numberFrom(chosenCase.spec, CASE_COOLER_HEIGHT);
      if (coolerHeight != null && caseMaxHeight != null) {
        var heightFits = coolerHeight <= caseMaxHeight;
        lines.push({
          state: heightFits ? 'ok' : 'warn',
          text: heightFits
            ? cooler.label + ' (' + coolerHeight + 'mm) fits ' + chosenCase.label + ' (up to ' + caseMaxHeight + 'mm).'
            : cooler.label + ' is ' + coolerHeight + 'mm tall, taller than ' + chosenCase.label +
              ' supports (up to ' + caseMaxHeight + 'mm). Please double-check before ordering.'
        });
      }
    }

    if (cpu && cooler) {
      var cpuTdp = tdpOf(cpu.spec);
      var coolerTdp = tdpOf(cooler.spec);
      if (cpuTdp != null && coolerTdp != null) {
        var coolerAdequate = coolerTdp >= cpuTdp;
        lines.push({
          state: coolerAdequate ? 'ok' : 'warn',
          text: coolerAdequate
            ? cooler.label + ' is rated for ' + coolerTdp + 'W, enough for this ' + cpuTdp + 'W processor.'
            : cooler.label + ' is rated for ' + coolerTdp + 'W, which may not be enough for this ' +
              cpuTdp + 'W processor. A higher-rated cooler would run cooler and quieter.'
        });
      }
    }

    if (power && power.ready && power.psu && power.psu.watts) {
      var powerState = power.level === 'high' ? 'bad' : power.level === 'medium' ? 'warn' : 'ok';
      var powerText = {
        low: 'Power draw (' + power.systemWatts + 'W) is comfortably within your ' + power.psu.watts + 'W power supply.',
        medium: 'Power draw (' + power.systemWatts + 'W) is close to your ' + power.psu.watts + 'W power supply’s limit — a higher-wattage PSU would give more headroom.',
        high: 'Power draw (' + power.systemWatts + 'W) is too close to your ' + power.psu.watts + 'W power supply’s limit. Choose a higher-wattage PSU.'
      }[power.level];
      lines.push({ state: powerState, text: powerText });
    }

    if (breakdown.reconciles === false) {
      lines.push({
        state: 'warn',
        text: 'The total on the page does not match the components listed. Refresh before ordering.'
      });
    }

    lines.push({ state: 'ok', text: 'Assembled, tested and supported by Wattec.' });

    var worst = 'ok';
    lines.forEach(function (line) {
      if (line.state === 'bad') worst = 'bad';
      else if (line.state === 'warn' && worst !== 'bad') worst = 'warn';
    });

    var heads = {
      ok: ['is-ok', 'Everything fits'],
      warn: ['is-warn', 'Worth a look'],
      bad: ['is-bad', 'Needs changing']
    };

    target.appendChild(el('div', 'wpb-sum__compat-head ' + heads[worst][0], heads[worst][1]));

    lines.forEach(function (line) {
      var row = el('div', 'wpb-sum__compat-line');
      var marks = { ok: '✓', warn: '⚠', bad: '✕' };
      row.appendChild(el('span', 'wpb-sum__mark is-' + line.state, marks[line.state]));
      row.appendChild(el('span', null, line.text));
      target.appendChild(row);
    });
  }

  // A plain marker so it is possible to tell, from the page itself, which
  // build of this file the browser is actually running.
  window.WattecPCBuilder = window.WattecPCBuilder || {};
  window.WattecPCBuilder.summaryBuild = 'picks-filter-3';

  function boot() {
    var root = document.querySelector('[data-wpb-summary-root]');
    if (!root || !window.WattecPCBuilder || !window.WattecPCBuilder.aris) return;
    var adapter = window.WattecPCBuilder.aris;

    adapter.ready().then(function () {
      root.classList.add('wpb');

      // Where this panel sits on the page is decided by the step guide,
      // which puts it in the rail beside the build (wattec-pc-guide.js).
      var card = el('div', 'wpb-summary-card');
      var heading = el('div', 'wpb-summary-card__head', 'Build check');
      var picks = el('div', 'wpb-picks');
      var power = el('div', 'wpb-power');
      var compat = el('div', 'wpb-sum__compat');
      card.appendChild(heading);
      card.appendChild(picks);
      card.appendChild(power);
      card.appendChild(compat);
      root.appendChild(card);

      var currency = config().currency || 'GBP';

      function render() {
        var powerEngine = window.WattecPCBuilder && window.WattecPCBuilder.power;
        var est = powerEngine ? powerEngine.estimate(adapter) : null;
        renderPicks(picks, adapter, currency);
        renderPower(power, est);
        renderCompat(compat, adapter, est);
      }

      render();
      adapter.observeChanges(render);
    }).catch(function (error) {
      root.hidden = true;
      if (window.console) console.warn('[Wattec summary]', error.message);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
